'use client'

import { useMemo, useRef } from 'react'

import { trackCustomEvent } from '@/hooks/client/use-analytics-engine'
import { useCompared } from '@/hooks/client/use-compared'
import { EventType } from '@/lib/analytics/events'
import type { FilterOptions } from '@/lib/directory/build-filter-options'
import { countFacets } from '@/lib/directory/facet-counts'
import { buildFacetLabels } from '@/lib/directory/facet-labels'
import { unpackReleases, type PackedDirectory } from '@/lib/directory/pack-releases'
import { DIRECTORY_PAGE_SIZE, filterReleases, paginateReleases, sortReleases } from '@/lib/directory/present-releases'

import { CompareDock } from './compare-dock'
import { FilterRail } from './filters/filter-rail'
import { FilterSheet } from './filters/filter-sheet'
import { ReleaseList } from './release-list'
import { ResultsPagination } from './results-pagination'
import { ResultsToolbar } from './results-toolbar'
import { trackDirectory } from './track-directory'
import { ActionType, useDirectoryReducer } from './use-directory-reducer'
import { useDirectoryUrl } from './use-directory-url'

interface DirectoryPageProps {
  /** Every comparable family, packed for the wire (see pack-releases.ts). */
  packed: PackedDirectory
  filterOptions: FilterOptions
  /** Families per page to start with (ten, as on the directory). */
  pageSize?: number
  /** Sizes the reader may switch between; none means no page-size control. */
  pageSizeOptions?: number[]
}

// The directory workspace: a filter rail (desktop) or sheet (phones), the
// results toolbar and rows, and the compare tray. Filter/sort/search state is
// local (the reducer); the compare selection is the shared useCompared store.
export const DirectoryPage = ({
  packed,
  filterOptions,
  pageSize = DIRECTORY_PAGE_SIZE,
  pageSizeOptions = [],
}: DirectoryPageProps) => {
  const releases = useMemo(() => unpackReleases(packed), [packed])
  const [state, dispatch] = useDirectoryReducer(pageSize)
  const { compared, toggle, atMax } = useCompared()
  const resultsRef = useRef<HTMLDivElement>(null)

  useDirectoryUrl(state, dispatch)

  const results = useMemo(
    () => sortReleases(filterReleases(releases, state.filters, state.term), state.sort),
    [releases, state.filters, state.term, state.sort]
  )
  // One page of rows renders; the full result set stays for the counts and
  // the tray. Filters, sort and search return the reducer to page 1.
  const paged = useMemo(
    () => paginateReleases(results, state.page, state.pageSize),
    [results, state.page, state.pageSize]
  )
  const modelCount = useMemo(() => results.reduce((sum, r) => sum + r.modelCount, 0), [results])
  const totalModelCount = useMemo(() => releases.reduce((sum, r) => sum + r.modelCount, 0), [releases])
  const facetLabels = useMemo(() => buildFacetLabels(filterOptions), [filterOptions])
  const counts = useMemo(
    () => countFacets(releases, filterOptions, state.filters, state.term),
    [releases, filterOptions, state.filters, state.term]
  )

  const handleToggleCompare = (slug: string) => {
    // Match the old site's compare-add-product--click event (fired only when a
    // product is added, not removed). trackCustomEvent passes the event name
    // through verbatim, so include the suffix the old `track()` helper added.
    if (!compared.has(slug)) {
      trackCustomEvent('compare-add-product--click', EventType.Click, { eventRelease: slug, origin: 'directory' })
    }
    toggle(slug)
  }

  // A page change lands the reader at the top of the new page's rows, with
  // focus on the results column so the next Tab reaches the first row.
  const handlePageChange = (page: number) => {
    trackDirectory('directory-page', { page })
    dispatch({ type: ActionType.SetPage, page })
    resultsRef.current?.scrollIntoView({ block: 'start' })
    resultsRef.current?.focus({ preventScroll: true })
  }

  const handlePageSizeChange = (size: number) => {
    trackDirectory('directory-page-size', { pageSize: size })
    dispatch({ type: ActionType.SetPageSize, pageSize: size })
  }
  // The per-page choice only shows when the smallest size would still page.
  const sizeOptions = pageSizeOptions.length > 0 && results.length > Math.min(...pageSizeOptions) ? pageSizeOptions : []

  return (
    // Bottom clearance follows the tray's measured height (a CSS variable it
    // publishes on <html>), so nothing hides behind it at any pick count.
    <div className="mt-8 pb-[calc(var(--compare-dock-h,0px)+1.5rem)] md:mt-10">
      <div className="lg:grid lg:grid-cols-[232px_minmax(0,1fr)] lg:gap-x-8 laptop:grid-cols-[264px_minmax(0,1fr)] laptop:gap-x-12">
        <FilterRail filters={state.filters} filterOptions={filterOptions} counts={counts} dispatch={dispatch} />
        <div ref={resultsRef} tabIndex={-1} className="min-w-0 scroll-mt-24 outline-none">
          <ResultsToolbar
            state={state}
            dispatch={dispatch}
            resultCount={results.length}
            totalCount={releases.length}
            modelCount={modelCount}
            totalModelCount={totalModelCount}
            facetLabels={facetLabels}
          />
          <ReleaseList
            releases={paged.items}
            compared={compared}
            atMax={atMax}
            facetLabels={facetLabels}
            onToggleCompare={handleToggleCompare}
            onClearFilters={() => dispatch({ type: ActionType.ClearFilters })}
          />
          {paged.pageCount > 1 || sizeOptions.length > 0 ? (
            <ResultsPagination
              paged={paged}
              onPageChange={handlePageChange}
              pageSizeChoice={
                sizeOptions.length > 0
                  ? { pageSize: state.pageSize, options: sizeOptions, onChange: handlePageSizeChange }
                  : undefined
              }
            />
          ) : null}
        </div>
      </div>
      <FilterSheet
        isOpen={state.isFilterModalOpen}
        filters={state.filters}
        sort={state.sort}
        filterOptions={filterOptions}
        counts={counts}
        dispatch={dispatch}
        resultCount={results.length}
      />
      <CompareDock compared={compared} releases={releases} filters={state.filters} onRemove={toggle} />
    </div>
  )
}
